import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DateTimeDisplay from './DateTimeDisplay';
import { API_ENDPOINTS } from '../config/api';

const API_BASE = `${API_ENDPOINTS.BASE_URL}/api`;

const ExpenseRequestDashboard = () => {
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestStats, setRequestStats] = useState({});
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharedUsers, setSharedUsers] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if admin is logged in
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin');
      return;
    }

    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPendingRequests(),
        loadRequestStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/requests/pending`);
      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data);
      }
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const loadRequestStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/requests/stats`);
      if (response.ok) {
        const data = await response.json();
        setRequestStats(data);
      }
    } catch (error) {
      console.error('Error loading request stats:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const getPriorityBadge = (priority) => {
    if (priority >= 8) return 'bg-red-100 text-red-800';
    if (priority >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  // Collaboration functions
  const loadComments = async (expenseId) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/${expenseId}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedExpense) return;

    try {
      const response = await fetch(`${API_BASE}/expenses/${selectedExpense.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comment: newComment,
          commentedBy: 'Admin User' // In real app, get from auth
        })
      });

      if (response.ok) {
        setNewComment('');
        loadComments(selectedExpense.id);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleShareExpense = async () => {
    if (!sharedUsers.trim() || !selectedExpense) return;

    try {
      const users = sharedUsers.split(',').map(user => user.trim());
      const response = await fetch(`${API_BASE}/expenses/${selectedExpense.id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sharedWith: users,
          sharedBy: 'Admin User' // In real app, get from auth
        })
      });

      if (response.ok) {
        setSharedUsers('');
        setShowShareModal(false);
        alert('Expense shared successfully!');
      }
    } catch (error) {
      console.error('Error sharing expense:', error);
    }
  };

  const handleBulkAction = async (action, expenseIds) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/requests/bulk-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          expenseIds,
          approvedBy: 'Admin User', // In real app, get from auth
          rejectionReason: action === 'reject' ? 'Bulk rejection' : undefined
        })
      });

      if (response.ok) {
        loadData(); // Refresh all data
        alert(`${expenseIds.length} expenses ${action}d successfully!`);
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const handleApproveExpense = async (expenseId) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/${expenseId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvedBy: 'Admin User' })
      });

      if (response.ok) {
        loadData();
        alert('Expense approved successfully!');
      }
    } catch (error) {
      console.error('Error approving expense:', error);
    }
  };

  const handleRejectExpense = async (expenseId, reason) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/${expenseId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejectedBy: 'Admin User',
          rejectionReason: reason
        })
      });

      if (response.ok) {
        loadData();
        alert('Expense rejected successfully!');
      }
    } catch (error) {
      console.error('Error rejecting expense:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading request dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Expense Approval Requests</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/expense-management')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-300"
            >
              Full Expense Dashboard
            </button>
            <DateTimeDisplay />
          </div>
        </div>

        {/* Request Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">{requestStats.pending || 0}</div>
            <div className="text-sm text-gray-600">Pending Requests</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-red-600">{requestStats.urgent || 0}</div>
            <div className="text-sm text-gray-600">Urgent Requests</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">{requestStats.overdue || 0}</div>
            <div className="text-sm text-gray-600">Overdue Requests</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-green-600">{requestStats.approved || 0}</div>
            <div className="text-sm text-gray-600">Approved Today</div>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 mb-8">
          <h2 className="text-xl font-semibold text-blue-800 mb-4">Bulk Actions</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => {
                const urgentIds = pendingRequests.filter(r => r.priority >= 8).map(r => r.id);
                if (urgentIds.length > 0) {
                  handleBulkAction('approve', urgentIds);
                } else {
                  alert('No urgent requests found.');
                }
              }}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition duration-300"
            >
              Approve Urgent ({requestStats.urgent || 0})
            </button>
            <button
              onClick={() => {
                const pendingIds = pendingRequests.map(r => r.id);
                if (pendingIds.length > 0) {
                  handleBulkAction('approve', pendingIds);
                } else {
                  alert('No pending requests.');
                }
              }}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition duration-300"
            >
              Approve All Pending ({requestStats.pending || 0})
            </button>
            <button
              onClick={() => {
                const highPriorityIds = pendingRequests.filter(r => r.priority >= 7).map(r => r.id);
                if (highPriorityIds.length > 0) {
                  handleBulkAction('approve', highPriorityIds);
                } else {
                  alert('No high priority requests found.');
                }
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
            >
              Approve High Priority
            </button>
          </div>
        </div>

        {/* Pending Requests */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-6">Pending Approval Requests</h2>

          {pendingRequests.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">Loading</div>
              <h3 className="text-xl font-medium text-gray-800 mb-2">All Caught Up!</h3>
              <p className="text-gray-600">No pending expense requests at this time.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingRequests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-800">{request.description}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityBadge(request.priority)}`}>
                          Priority {request.priority}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Vendor:</strong> {request.vendor}</p>
                        <p><strong>Category:</strong> {request.category.replace('_', ' ')}</p>
                        <p><strong>Submitted:</strong> {request.daysPending} days ago</p>
                        <p><strong>Department:</strong> {request.department}</p>
                      </div>
                    </div>
                    <div className="text-right ml-6">
                      <div className="text-2xl font-bold text-green-600 mb-2">{formatCurrency(request.amount)}</div>
                      {request.daysPending > 7 && (
                        <span className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium">
                          OVERDUE
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleApproveExpense(request.id)}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition duration-300 font-medium"
                    >
                      ? Approve
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Please provide a reason for rejection:');
                        if (reason && reason.trim()) {
                          handleRejectExpense(request.id, reason.trim());
                        }
                      }}
                      className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition duration-300 font-medium"
                    >
                      X Reject
                    </button>
                    <button
                      onClick={() => {
                        setSelectedExpense(request);
                        setShowCommentModal(true);
                        loadComments(request.id);
                      }}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition duration-300 font-medium"
                    >
                      💬 Comments ({request.comments?.length || 0})
                    </button>
                    <button
                      onClick={() => {
                        setSelectedExpense(request);
                        setShowShareModal(true);
                      }}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition duration-300 font-medium"
                    >
                      Students Share
                    </button>
                    <button
                      onClick={() => navigate(`/admin/expense-management`)}
                      className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition duration-300 font-medium"
                    >
                      Stats View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comment Modal */}
        {showCommentModal && selectedExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-blue-800">Comments - {selectedExpense.description}</h3>
                <button
                  onClick={() => setShowCommentModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4 mb-4">
                {comments.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No comments yet. Be the first to comment!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-gray-800">{comment.commentedBy}</span>
                        <span className="text-sm text-gray-500">
                          {new Date(comment.commentedAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-700">{comment.comment}</p>
                    </div>
                  ))
                )}
              </div>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <button
                  onClick={handleAddComment}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && selectedExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-blue-800">Share Expense Request</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Share the expense request "{selectedExpense.description}" with other stakeholders for review.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email addresses (comma-separated):
                </label>
                <textarea
                  value={sharedUsers}
                  onChange={(e) => setSharedUsers(e.target.value)}
                  placeholder="manager@school.com, finance@school.com, director@school.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareExpense}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition duration-300"
                >
                  Share Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseRequestDashboard;
